import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Grid,
    InputAdornment,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import SearchIcon from '@material-ui/icons/Search';
import { getStaffFacturas } from '../../services/staff-lector';

const useStyles = makeStyles(theme => ({
    topActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2.5),
        flexWrap: 'wrap',
    },
    searchField: {
        minWidth: 300,
        '& .MuiOutlinedInput-root': {
            borderRadius: 5,
            backgroundColor: theme.palette.surface.popover,
        },
        '& .MuiOutlinedInput-input': {
            paddingTop: theme.spacing(1.1),
            paddingBottom: theme.spacing(1.1),
        },
    },
    secondaryButton: {
        borderRadius: 5,
        padding: theme.spacing(1, 2.25),
        textTransform: 'none',
        fontWeight: 700,
        border: `1px solid ${theme.palette.border.button}`,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.surface.alt,
        boxShadow: theme.palette.shadow.card,
        '&:hover': { backgroundColor: theme.palette.surface.neutral },
    },
    primaryButton: {
        borderRadius: 5,
        padding: theme.spacing(1, 2.25),
        textTransform: 'none',
        fontWeight: 700,
        color: theme.palette.common.white,
        background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: theme.palette.shadow.accent,
        '&:hover': {
            background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        },
    },
    countLabel: {
        marginLeft: 'auto',
        fontSize: 13,
        color: theme.palette.text.disabled,
    },
    filtersPanel: {
        marginBottom: theme.spacing(2.5),
        padding: theme.spacing(2.5),
        border: `1px solid ${theme.palette.border.main}`,
        borderRadius: 5,
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.palette.shadow.search,
    },
    filterLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.75),
    },
    filterField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 5,
            backgroundColor: theme.palette.surface.popover,
            fontSize: 13,
        },
        '& .MuiOutlinedInput-input': {
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
    },
    listContainer: {
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
    },
    tableHead: {
        backgroundColor: theme.palette.surface.panel,
    },
    headCell: {
        color: theme.palette.text.section,
        fontWeight: 700,
        borderBottom: `2px solid ${theme.palette.border.main}`,
        whiteSpace: 'nowrap',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    bodyRow: {
        '&:nth-child(even)': {
            backgroundColor: theme.palette.surface.soft,
        },
    },
    bodyCell: {
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    clientCell: {
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        fontWeight: 700,
        color: theme.palette.grey.main,
        whiteSpace: 'nowrap',
        maxWidth: 200,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    filename: {
        maxWidth: 160,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        color: theme.palette.text.secondary,
        fontSize: 12,
    },
    statusChip: {
        fontWeight: 600,
        height: 24,
        borderRadius: 4,
        fontSize: 11,
        '& .MuiChip-label': {
            paddingLeft: theme.spacing(1.25),
            paddingRight: theme.spacing(1.25),
        },
    },
    scoreHigh: { color: theme.palette.status.success, fontWeight: 700 },
    scoreMid: { color: theme.palette.warning.main, fontWeight: 700 },
    scoreLow: { color: theme.palette.error.main, fontWeight: 700 },
    centeredState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 180,
    },
    pagination: {
        borderTop: `1px solid ${theme.palette.border.subtle}`,
    },
}));

const STATUS_COLORS = {
    OK: { bg: '#dcfce7', color: '#047857' },
    ERROR: { bg: '#fee2e2', color: '#b91c1c' },
    PROCESSING: { bg: '#fef3c7', color: '#b45309' },
    ABORTADO: { bg: '#f3f4f6', color: '#6b7280' },
};

function EstadoChip({ estado }) {
    const classes = useStyles();
    if (estado == null) return <span style={{ color: '#9ca3af' }}>—</span>;
    const colors = STATUS_COLORS[estado] || { bg: '#f3f4f6', color: '#6b7280' };
    return (
        <Chip
            label={estado}
            className={classes.statusChip}
            size="small"
            style={{ backgroundColor: colors.bg, color: colors.color }}
        />
    );
}

function ScoreCell({ score }) {
    const classes = useStyles();
    if (score == null) return <span style={{ color: '#9ca3af' }}>—</span>;
    const cls = score >= 80 ? classes.scoreHigh : score >= 50 ? classes.scoreMid : classes.scoreLow;
    return <span className={cls}>{score.toFixed(1)}</span>;
}

function fmt(n, d = 0) {
    if (n == null) return '—';
    return Number(n).toLocaleString('es-AR', { minimumFractionDigits: d, maximumFractionDigits: d });
}

const EMPTY_FILTERS = {
    tipo: '',
    calidad: '',
    estado: '',
    es_huerfana: '',
    periodo_desde: '',
    periodo_hasta: '',
    fecha_recepcion_desde: '',
    fecha_recepcion_hasta: '',
};

export default function FacturasTab() {
    const classes = useStyles();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [applied, setApplied] = useState({ search: '', ...EMPTY_FILTERS });
    const [page, setPage] = useState(0);
    const [pageSize] = useState(20);
    const [orderBy, setOrderBy] = useState('score');
    const [orderDir, setOrderDir] = useState('asc');

    const fetchData = useCallback(() => {
        setLoading(true);
        getStaffFacturas({ ...applied, ordenar_por: orderBy, ordenar_dir: orderDir, page: page + 1, page_size: pageSize })
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [applied, orderBy, orderDir, page, pageSize]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSort = col => {
        if (orderBy === col) setOrderDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setOrderBy(col); setOrderDir('asc'); }
        setPage(0);
    };

    const handleApply = e => {
        e.preventDefault();
        setApplied({ search: searchInput, ...filters });
        setPage(0);
    };

    const handleClear = () => {
        setSearchInput('');
        setFilters(EMPTY_FILTERS);
        setApplied({ search: '', ...EMPTY_FILTERS });
        setPage(0);
    };

    const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

    return (
        <Box>
            <Box className={classes.topActions}>
                <form onSubmit={handleApply} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <TextField
                        className={classes.searchField}
                        variant="outlined"
                        size="small"
                        placeholder="Proveedor / N° de factura / Cliente"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="disabled" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        className={classes.secondaryButton}
                        startIcon={<FilterListIcon />}
                        onClick={() => setFiltersOpen(o => !o)}
                        size="small"
                    >
                        Filtros
                    </Button>
                    <Button type="submit" className={classes.primaryButton} size="small">
                        Buscar
                    </Button>
                    <Button size="small" onClick={handleClear} className={classes.secondaryButton}>
                        Limpiar
                    </Button>
                </form>
                {data && (
                    <Typography className={classes.countLabel}>
                        {fmt(data.count)} factura{data.count !== 1 ? 's' : ''}
                    </Typography>
                )}
            </Box>

            <Collapse in={filtersOpen}>
                <Box className={classes.filtersPanel}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography className={classes.filterLabel}>Tipo de servicio</Typography>
                            <Select value={filters.tipo} onChange={e => setF('tipo', e.target.value)} variant="outlined" fullWidth displayEmpty className={classes.filterField}>
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="electricidad">Electricidad</MenuItem>
                                <MenuItem value="gas">Gas</MenuItem>
                                <MenuItem value="agua">Agua</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography className={classes.filterLabel}>Calidad de score</Typography>
                            <Select value={filters.calidad} onChange={e => setF('calidad', e.target.value)} variant="outlined" fullWidth displayEmpty className={classes.filterField}>
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="alto">Alta (≥80)</MenuItem>
                                <MenuItem value="medio">Media (50–79)</MenuItem>
                                <MenuItem value="bajo">Baja (&lt;50)</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography className={classes.filterLabel}>Estado</Typography>
                            <Select value={filters.estado} onChange={e => setF('estado', e.target.value)} variant="outlined" fullWidth displayEmpty className={classes.filterField}>
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="OK">OK</MenuItem>
                                <MenuItem value="ERROR">Error</MenuItem>
                                <MenuItem value="PROCESSING">Procesando</MenuItem>
                                <MenuItem value="ABORTADO">Abortado</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography className={classes.filterLabel}>Huérfanas</Typography>
                            <Select value={filters.es_huerfana} onChange={e => setF('es_huerfana', e.target.value)} variant="outlined" fullWidth displayEmpty className={classes.filterField}>
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="true">Solo huérfanas</MenuItem>
                                <MenuItem value="false">Con localización</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography className={classes.filterLabel}>Período desde</Typography>
                            <TextField type="date" value={filters.periodo_desde} onChange={e => setF('periodo_desde', e.target.value)} variant="outlined" fullWidth size="small" className={classes.filterField} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography className={classes.filterLabel}>Período hasta</Typography>
                            <TextField type="date" value={filters.periodo_hasta} onChange={e => setF('periodo_hasta', e.target.value)} variant="outlined" fullWidth size="small" className={classes.filterField} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                            <Typography className={classes.filterLabel}>Recepción desde</Typography>
                            <TextField type="date" value={filters.fecha_recepcion_desde} onChange={e => setF('fecha_recepcion_desde', e.target.value)} variant="outlined" fullWidth size="small" className={classes.filterField} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                            <Typography className={classes.filterLabel}>Recepción hasta</Typography>
                            <TextField type="date" value={filters.fecha_recepcion_hasta} onChange={e => setF('fecha_recepcion_hasta', e.target.value)} variant="outlined" fullWidth size="small" className={classes.filterField} InputLabelProps={{ shrink: true }} />
                        </Grid>
                    </Grid>
                </Box>
            </Collapse>

            <TableContainer className={classes.listContainer}>
                <Table size="small">
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={classes.headCell}>Cliente</TableCell>
                            <TableCell className={classes.headCell}>Archivo</TableCell>
                            <TableCell className={classes.headCell}>
                                <TableSortLabel active={orderBy === 'proveedor'} direction={orderBy === 'proveedor' ? orderDir : 'asc'} onClick={() => handleSort('proveedor')}>
                                    Proveedor
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.headCell}>Tipo</TableCell>
                            <TableCell className={classes.headCell}>Nro. factura</TableCell>
                            <TableCell className={classes.headCell}>
                                <TableSortLabel active={orderBy === 'periodo_desde'} direction={orderBy === 'periodo_desde' ? orderDir : 'asc'} onClick={() => handleSort('periodo_desde')}>
                                    Período
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.headCell} align="right">
                                <TableSortLabel active={orderBy === 'total'} direction={orderBy === 'total' ? orderDir : 'asc'} onClick={() => handleSort('total')}>
                                    Total
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.headCell} align="right">
                                <TableSortLabel active={orderBy === 'score'} direction={orderBy === 'score' ? orderDir : 'asc'} onClick={() => handleSort('score')}>
                                    Score
                                </TableSortLabel>
                            </TableCell>
                            <TableCell className={classes.headCell} align="center">Estado</TableCell>
                            <TableCell className={classes.headCell}>
                                <TableSortLabel active={orderBy === 'fecha_recepcion'} direction={orderBy === 'fecha_recepcion' ? orderDir : 'asc'} onClick={() => handleSort('fecha_recepcion')}>
                                    Recepción
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={10}>
                                    <Box className={classes.centeredState}>
                                        <CircularProgress size={28} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (data?.results || []).map(f => (
                            <TableRow key={f.record_id} className={classes.bodyRow}>
                                <TableCell className={classes.clientCell}>
                                    <Tooltip title={f.cliente_email || ''} arrow>
                                        <span>{f.cliente_email || '—'}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell className={classes.bodyCell}>
                                    <Tooltip title={f.filename || ''} arrow>
                                        <span className={classes.filename}>{f.filename || '—'}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell className={classes.bodyCell}>{f.proveedor_nombre || '—'}</TableCell>
                                <TableCell className={classes.bodyCell} style={{ textTransform: 'capitalize' }}>
                                    {f.tipo_servicio || f.tipo || '—'}
                                </TableCell>
                                <TableCell className={classes.bodyCell}>{f.numero_factura || '—'}</TableCell>
                                <TableCell className={classes.bodyCell}>
                                    {f.periodo_desde || '—'}{f.periodo_hasta ? ` → ${f.periodo_hasta}` : ''}
                                </TableCell>
                                <TableCell className={classes.bodyCell} align="right" style={{ fontWeight: 600 }}>
                                    {f.total != null ? `${f.moneda === 'USD' ? 'US$' : '$'} ${fmt(f.total, 2)}` : '—'}
                                </TableCell>
                                <TableCell className={classes.bodyCell} align="right">
                                    <ScoreCell score={f.score} />
                                </TableCell>
                                <TableCell className={classes.bodyCell} align="center">
                                    <EstadoChip estado={f.estado} />
                                </TableCell>
                                <TableCell className={classes.bodyCell}>
                                    {f.fecha_recepcion ? new Date(f.fecha_recepcion).toLocaleDateString('es-AR') : '—'}
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && data?.results?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={10} align="center" style={{ padding: 24 }}>
                                    <Typography variant="body2" color="textSecondary">Sin resultados</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {data && (
                    <TablePagination
                        className={classes.pagination}
                        component="div"
                        count={data.count}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={[20]}
                        onPageChange={(_, p) => setPage(p)}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                    />
                )}
            </TableContainer>
        </Box>
    );
}
